import React, { useState, useEffect } from 'react';
import {
  X,
  Download,
  FileQuestion,
  Loader2,
  SquareArrowOutUpRight,
  ArrowLeft
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../UI/dialog';
import { Drawer, DrawerContent } from '../UI/drawer';
import { Button } from '../UI/button';
import { getUploadUrl } from '../../Utils/Utilities';
import useIsMobile from '../../Hooks/useIsMobile';

/**
 * Normalizes file path to a full accessible URL
 */
export const resolveFileUrl = (filePath) => {
  if (!filePath) return '';
  const cleanPath = String(filePath).startsWith('/') ? String(filePath).slice(1) : String(filePath);
  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
    return cleanPath;
  }
  const uploadPath = cleanPath.startsWith('Uploads/') ? cleanPath : `Uploads/${cleanPath}`;
  return getUploadUrl(uploadPath);
};

/**
 * Extracts clean filename and file extension
 */
export const getFileMetadata = (filePath) => {
  if (!filePath) return { fileName: 'Attachment', extension: '', type: 'unknown', isImage: false, isPdf: false };
  const rawPath = String(filePath).split('?')[0];
  const parts = rawPath.split('/');
  const fullFileName = parts[parts.length - 1] || 'Attachment';
  const dotIndex = fullFileName.lastIndexOf('.');
  const extension = dotIndex !== -1 ? fullFileName.slice(dotIndex + 1).toLowerCase() : '';

  // Classify file type: only Image and PDF are previewable
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];

  const isImage = imageExts.includes(extension);
  const isPdf = pdfExts.includes(extension);

  return {
    fileName: fullFileName,
    extension,
    type: isImage ? 'image' : isPdf ? 'pdf' : 'other',
    isImage,
    isPdf
  };
};

const useVisualViewportHeight = () => {
  const [height, setHeight] = useState(() => (typeof window !== 'undefined' && window.visualViewport ? window.visualViewport.height : 700));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const handleResize = () => {
      setHeight(window.visualViewport.height);
    };
    window.visualViewport.addEventListener('resize', handleResize);
    return () => window.visualViewport.removeEventListener('resize', handleResize);
  }, []);

  return height;
};

export default function AttachmentPreviewModal({
  open,
  onOpenChange,
  filePath,
  fileName: customFileName
}) {
  const isMobile = useIsMobile(640);
  const vvHeight = useVisualViewportHeight();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Reset states when filePath or open changes
  useEffect(() => {
    if (open) {
      setLoading(true);
      setLoadError(false);
      setDownloading(false);
    }
  }, [open, filePath]);

  if (!filePath) return null;

  const fullUrl = resolveFileUrl(filePath);
  const meta = getFileMetadata(filePath);
  const displayName = customFileName || meta.fileName;

  const handleDownload = async (e) => {
    e?.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(fullUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Download request failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      // Direct anchor click fallback
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = displayName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInNewTab = (e) => {
    e?.stopPropagation();
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  // Preview Canvas Body
  const renderPreviewBody = () => (
    <div className="flex-1 min-h-0 bg-[var(--background)] relative overflow-hidden flex flex-col px-3 pb-3 sm:p-4">
      {/* 1. Image Preview */}
      {meta.isImage && !loadError && (
        <div className="w-full flex-1 min-h-0 rounded-lg flex items-center justify-center overflow-hidden p-2 sm:p-3">
          <img
            src={fullUrl}
            alt={displayName}
            className={`max-w-full max-h-full w-auto h-auto object-contain rounded-md transition-opacity duration-200 ${loading ? 'opacity-0' : 'opacity-100'}`}
            style={{
              maxHeight: isMobile ? `${vvHeight * 0.85 - 140}px` : 'calc(85vh - 140px)'
            }}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setLoadError(true);
            }}
          />
        </div>
      )}

      {/* 2. PDF Preview (Native Browser Viewer with FitH) */}
      {meta.isPdf && !loadError && (
        <div className="w-full h-full bg-white rounded-lg border border-sahi-border-color shadow-2xs overflow-hidden flex flex-col relative">
          <iframe
            src={`${fullUrl}#view=FitH&toolbar=0&navpanes=0`}
            title={displayName}
            className="w-full h-full border-0 flex-1 bg-white"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setLoadError(true);
            }}
          />
        </div>
      )}

      {/* 3. Fallback for Unsupported types or Error */}
      {(loadError || (!meta.isImage && !meta.isPdf)) && (
        <div className="w-full h-full bg-white rounded-lg border border-sahi-border-color shadow-2xs flex flex-col items-center justify-center text-center p-6">
          <FileQuestion className="h-8 w-8 text-sub-text-color mb-2" />
          <p className="text-sm font-semibold text-text-color">
            {loadError ? 'Preview unavailable' : 'Direct preview not supported'}
          </p>
          <p className="text-xs text-sub-text-color mt-1 max-w-sm">
            {loadError
              ? 'Unable to load document preview. Use the actions below to open or download the file.'
              : `Preview is not supported for ${meta.extension ? `.${meta.extension}` : 'this'} file. You can open or download it using the actions below.`}
          </p>
        </div>
      )}
    </div>
  );

  // Modal / Drawer Footer Buttons
  const renderFooterButtons = () => (
    <div className="bg-background rounded-b-[22px] sm:rounded-lg px-4 pb-4 pt-1 flex items-center gap-2 shrink-0">
      <Button
        onClick={handleDownload}
        disabled={downloading}
        className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green flex-1 sm:flex-none sm:min-w-[110px]"
      >
        {downloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span>{downloading ? 'Downloading...' : 'Download'}</span>
      </Button>

      <Button
        variant="outline"
        onClick={handleOpenInNewTab}
        className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
      >
        <SquareArrowOutUpRight className="h-4 w-4" /> Open
      </Button>
    </div>
  );

  // Mobile: Bottom Drawer with header matching TaskCategoryManager
  if (isMobile) {
    const isImageAttachment = meta.isImage && !loadError;
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden bg-[var(--background)]"
          style={{
            maxHeight: `${vvHeight * 0.85}px`,
            height: isImageAttachment ? 'auto' : `${vvHeight * 0.65}px`
          }}
        >
          <div
            className="flex flex-col w-full"
            style={{
              maxHeight: `${vvHeight * 0.85}px`,
              height: isImageAttachment ? 'auto' : '100%'
            }}
          >
            {/* Mobile Header with Back Arrow and File Name matching TaskCategoryManager */}
            <div className="flex items-center justify-between px-4 h-16 shrink-0 bg-[var(--background)]">
              <div className="flex gap-4 items-center min-w-0 flex-1 mr-2">
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => onOpenChange(false)}
                  className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 cursor-pointer"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-[24px] w-[24px]" />
                </button>
                <p className="text-lg font-semibold text-gray-900 truncate" title={displayName}>
                  {displayName}
                </p>
              </div>
            </div>

            {/* Content Preview Canvas */}
            {renderPreviewBody()}

            {/* Action Buttons Footer */}
            {renderFooterButtons()}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Standard Dialog
  const isImageAttachment = meta.isImage && !loadError;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isImageAttachment
            ? "!w-auto !min-w-[340px] sm:!max-w-[760px] !h-auto !min-h-[260px] sm:!max-h-[85vh] flex flex-col gap-0 bg-[var(--background)] p-0 rounded-xl border border-sahi-border-color shadow-2xl overflow-hidden z-[10000]"
            : "!w-[92vw] sm:!max-w-[760px] !h-[85vh] sm:!max-h-[720px] flex flex-col gap-0 bg-[var(--background)] p-0 rounded-xl border border-sahi-border-color shadow-2xl overflow-hidden z-[10000]"
        }
      >
        {/* Modal Header: Styled matching TaskDetails header */}
        <DialogHeader className="border-b px-4 py-3 flex flex-row items-center justify-between space-y-0 bg-white shrink-0">
          <div className="flex items-center min-w-0 flex-1 mr-2">
            <DialogTitle className="text-base md:text-lg font-semibold text-text-color truncate" title={displayName}>
              {displayName}
            </DialogTitle>
          </div>

          <DialogClose className="flex-shrink-0 cursor-pointer" tabIndex={-1}>
            <X className="h-5 w-5" />
          </DialogClose>
        </DialogHeader>

        {/* Modal Body Canvas */}
        {renderPreviewBody()}

        {/* Modal Footer */}
        {renderFooterButtons()}
      </DialogContent>
    </Dialog>
  );
}

